import React, { useState, useEffect } from 'react';
import { Plus, Mail, RefreshCw, Trash2, Clock, CheckCircle, XCircle, Search, Eye, Users, UserCheck, UserX, UserMinus, Shield, RotateCcw } from 'lucide-react';
import { Modal } from './Modal';
import { NotificationModal } from '../NotificationModal';
import { DangerModal } from '../DangerModal';
import { isClient, isBuiltInAdmin } from '../../utils/userRoleHelpers';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  user_type?: 'client' | 'staff';
  roles?: string[];
  status: string;
  phone?: string;
  created_at: string;
  token_expires_at?: string;
  tokenExpired?: boolean;
  last_login_at?: string;
}

interface PendingInvitation extends User {
  tokenExpired: boolean;
}

interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  pendingInvitations: number;
  inactiveUsers: number;
  usersByRole: {
    clients: number;
    coaches: number;
    admins: number;
  };
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState<'all' | 'clients' | 'pending'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, _setStatusFilter] = useState<'all' | 'active' | 'pending' | 'inactive'>('all');
  const [roleFilter, _setRoleFilter] = useState<'all' | 'admin' | 'staff' | 'client'>('all');

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'deactivate' | 'activate';
    user: User | null;
  }>({ isOpen: false, type: 'deactivate', user: null });

  // Danger modal state for delete operations
  const [dangerModal, setDangerModal] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({ isOpen: false, user: null });

  // Notification modal state
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Form state for new user invitation
  const [inviteForm, setInviteForm] = useState<InviteUserData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'client'
  });

  const [formErrors, setFormErrors] = useState<Partial<InviteUserData>>({});

  // Edit mode state for User Details Modal
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    roles: [] as string[]
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Available roles loaded from schema
  const [availableStaffRoles, setAvailableStaffRoles] = useState<string[]>([]);


  useEffect(() => {
    loadData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAllUsers(),
        loadDashboardStats(),
        loadPendingInvitations(),
        loadRolesSchema()
      ]);
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRolesSchema = async () => {
    try {
      const response = await fetch('/api/admin/roles/schema', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const roleIds = data.data.staff_roles.map((role: any) => role.id);
        setAvailableStaffRoles(roleIds);
      }
    } catch (error) {
      console.error('Failed to load roles schema:', error);
      // Fallback to hardcoded roles if schema fails to load
      setAvailableStaffRoles(['admin', 'coach']);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load all users:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const response = await fetch('/api/admin/clients/pending', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(data.pendingInvitations || []);
      }
    } catch (error) {
      console.error('Failed to load pending invitations:', error);
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<InviteUserData> = {};

    if (!inviteForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) {
      errors.email = 'Valid email address required';
    }

    if (!inviteForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!inviteForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInviteUser = async () => {
    if (!validateForm()) return;

    try {
      const response = await fetch('/api/admin/clients/invite', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(inviteForm),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ User invitation sent successfully');
        setInviteModalOpen(false);
        setInviteForm({
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          role: 'client'
        });
        setFormErrors({});
        
        // Refresh data
        await loadData();
      } else {
        console.error('Failed to send invitation:', data.error);
        alert(`Failed to send invitation: ${data.error}`);
      }
    } catch (error) {
      console.error('Invitation error:', error);
      alert('Failed to send invitation. Please try again.');
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      const response = await fetch('/api/admin/clients/resend-invitation', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Invitation resent successfully');
        await loadData();
      } else {
        alert(`Failed to resend invitation: ${data.error}`);
      }
    } catch (error) {
      console.error('Resend invitation error:', error);
      alert('Failed to resend invitation. Please try again.');
    }
  };


  const handleViewUserDetails = (user: User) => {
    setSelectedUser(user);
    setUserDetailsModalOpen(true);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
  };

  const handleCloseUserDetails = () => {
    setSelectedUser(null);
    setUserDetailsModalOpen(false);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
  };

  const handleEnterEditMode = () => {
    if (selectedUser) {
      setEditForm({
        first_name: selectedUser.first_name || '',
        last_name: selectedUser.last_name || '',
        email: selectedUser.email || '',
        phone: selectedUser.phone || '',
        roles: selectedUser.roles || []
      });
      setIsEditMode(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setHasUnsavedChanges(false);
  };

  const handleEditFormChange = (field: string, value: string | string[]) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleToggleRole = (role: string) => {
    setEditForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveUserChanges = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const data = await response.json();

        // Update selectedUser with the new data from server
        setSelectedUser({
          ...selectedUser,
          ...data.user
        });

        setNotificationModal({
          isOpen: true,
          type: 'success',
          title: 'User Updated',
          message: 'User information has been successfully updated.'
        });
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        loadData(); // Reload user list in background
      } else {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      setNotificationModal({
        isOpen: true,
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update user information. Please try again.'
      });
    }
  };

  const handleToggleUserStatus = (user: User) => {
    if (user.status === 'active') {
      setConfirmationModal({ isOpen: true, type: 'deactivate', user });
    } else {
      setConfirmationModal({ isOpen: true, type: 'activate', user });
    }
  };

  const handleDeleteUser = (user: User) => {
    setDangerModal({ isOpen: true, user });
  };

  const handleResetWelcomeModal = async (user: User) => {
    try {
      const response = await fetch(`/api/admin/users/${user.id}/reset-welcome`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setNotificationModal({
          isOpen: true,
          type: 'success',
          title: 'Welcome Modal Reset',
          message: `Welcome modal has been reset for ${user.first_name} ${user.last_name}. They will see the welcome modal on their next login.`
        });
      } else {
        throw new Error('Failed to reset welcome modal');
      }
    } catch (error) {
      console.error('Error resetting welcome modal:', error);
      setNotificationModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to reset welcome modal. Please try again.'
      });
    }
  };

  const handleRegenerateInvitation = async (userId: string) => {
    try {
      const response = await fetch('/api/admin/clients/regenerate-invitation', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (response.ok) {
        setNotificationModal({
          isOpen: true,
          type: 'success',
          title: 'Invitation Regenerated',
          message: data.message + ' A new verification token has been generated.'
        });
        await loadData(); // Refresh the user list
      } else {
        throw new Error(data.error || 'Failed to regenerate invitation');
      }
    } catch (error) {
      console.error('Error regenerating invitation:', error);
      setNotificationModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to regenerate invitation. Please try again.'
      });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmationModal.user) return;
    
    const { type, user } = confirmationModal;
    
    try {
      const newStatus = type === 'activate' ? 'active' : 'inactive';
      const response = await fetch(`/api/admin/users/${user.id}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log(`✅ User ${type}d successfully`);
        await loadData();
      } else {
        alert(`Failed to ${type} user: ${data.error}`);
      }
    } catch (error) {
      console.error(`Failed to ${type} user:`, error);
      alert(`Failed to ${type} user. Please try again.`);
    } finally {
      setConfirmationModal({ isOpen: false, type: 'deactivate', user: null });
    }
  };

  const handleCancelAction = () => {
    setConfirmationModal({ isOpen: false, type: 'deactivate', user: null });
  };

  const handleConfirmDelete = async () => {
    if (!dangerModal.user) return;

    try {
      const response = await fetch(`/api/admin/users/${dangerModal.user.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ User deleted successfully');
        await loadData();
      } else {
        alert(`Failed to delete user: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setDangerModal({ isOpen: false, user: null });
    }
  };

  const handleCancelDelete = () => {
    setDangerModal({ isOpen: false, user: null });
  };

  // Filter functions
  const getFilteredPendingInvitations = () => {
    return pendingInvitations.filter(invitation => {
      const matchesSearch = invitation.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invitation.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invitation.last_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      
      return matchesSearch && matchesStatus && matchesRole;
    });
  };

  const getFilteredClients = () => {
    return users.filter(user => {
      // Show all client users regardless of status
      const userIsClient = isClient(user);

      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase());

      return userIsClient && matchesSearch;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusIcon = (status: string, expired?: boolean) => {
    if (status === 'pending_verification') {
      return expired ? <XCircle size={16} /> : <Clock size={16} />;
    }
    if (status === 'active') {
      return <CheckCircle size={16} />;
    }
    return <XCircle size={16} />;
  };

  const getStatusColor = (status: string, expired?: boolean) => {
    if (status === 'pending_verification') {
      return expired ? 'status-expired' : 'status-pending';
    }
    if (status === 'active') {
      return 'status-active';
    }
    return 'status-inactive';
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'staff': return 'role-staff';
      case 'coach': return 'role-coach';
      case 'client': return 'role-client';
      default: return 'role-default';
    }
  };

  const renderUserTable = (users: User[], isPending = false) => {
    if (users.length === 0) {
      return (
        <div className="empty-state">
          <Users size={48} />
          <h3>No Users Found</h3>
          <p>{isPending ? 'No pending invitations to display.' : 'No users match your current filters.'}</p>
        </div>
      );
    }

    return (
      <div className="table-container">
        <div className="table-header">
          <div className="table-row header-row">
            <div className="table-cell">Name</div>
            <div className="table-cell">Email</div>
            <div className="table-cell">Roles</div>
            <div className="table-cell">Status</div>
            <div className="table-cell">Last Login</div>
            <div className="table-cell">Actions</div>
          </div>
        </div>
        <div className="table-body">
          {users.map((user) => (
            <div key={user.id} className="table-row data-row">
              <div className="table-cell name-cell">
                <div className="user-name">{user.first_name} {user.last_name}</div>
              </div>
              <div className="table-cell email-cell">
                <div className="user-email">{user.email}</div>
              </div>
              <div className="table-cell roles-cell">
                {user.roles && user.roles.length > 0 ? (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {user.roles.map(role => (
                      <span key={role} className={`role-badge ${getRoleBadgeClass(role)}`}>
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={`role-badge ${getRoleBadgeClass(user.user_type || user.role || 'client')}`}>
                    {user.user_type || user.role || 'client'}
                  </span>
                )}
              </div>
              <div className="table-cell">
                <span className={`status-badge ${getStatusColor(user.status, user.tokenExpired)}`}>
                  {getStatusIcon(user.status, user.tokenExpired)}
                  {user.status === 'pending_verification' ?
                    (user.tokenExpired ? 'Expired' : 'Pending') :
                    user.status}
                </span>
              </div>
              <div className="table-cell">
                {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
              </div>
              <div className="table-cell actions-cell">
                <div className="action-buttons">
                  <button 
                    className="action-btn" 
                    title="View Details"
                    onClick={() => handleViewUserDetails(user)}
                  >
                    <Eye size={16} />
                  </button>
                  {user.status === 'pending_verification' && (
                    <>
                      <button
                        className="action-btn"
                        title="Resend Welcome Email"
                        onClick={() => handleResendInvitation(user.id)}
                      >
                        <Mail size={16} />
                      </button>
                      <button
                        className="action-btn"
                        title="Reset Welcome Email (New Token)"
                        onClick={() => handleRegenerateInvitation(user.id)}
                      >
                        <RefreshCw size={16} />
                      </button>
                    </>
                  )}
                  {user.status !== 'pending_verification' && (
                    <>
                      {!isBuiltInAdmin(user) && (
                        <button
                          className={`action-btn ${user.status === 'inactive' ? 'success' : 'warning'}`}
                          title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                          onClick={() => handleToggleUserStatus(user)}
                        >
                          {user.status === 'active' ? <UserMinus size={16} /> : <UserCheck size={16} />}
                        </button>
                      )}
                      {isClient(user) && user.status !== 'pending_verification' && (
                        <button
                          className="action-btn info"
                          title="Reset Welcome Modal"
                          onClick={() => handleResetWelcomeModal(user)}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </>
                  )}
                  {!isBuiltInAdmin(user) && (
                    <button
                      className="action-btn danger"
                      title="Delete User"
                      onClick={() => handleDeleteUser(user)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  {isBuiltInAdmin(user) && (
                    <button className="action-btn disabled" title="System admin account is protected">
                      <Shield size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="user-management">
      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon active">
              <UserCheck size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.activeUsers}</div>
              <div className="stat-label">Active Users</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon pending">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.pendingInvitations}</div>
              <div className="stat-label">Pending Invitations</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon inactive">
              <UserX size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.inactiveUsers}</div>
              <div className="stat-label">Inactive Users</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1>User Management</h1>
          <p>Manage client invitations and user accounts</p>
        </div>
        <div className="header-actions">
          <button
            className="invite-btn primary"
            onClick={() => setInviteModalOpen(true)}
          >
            <Plus size={20} />
            Invite New User
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="controls">
        <div className="view-tabs">
          <button 
            className={`tab ${currentView === 'all' ? 'active' : ''}`}
            onClick={() => setCurrentView('all')}
          >
            All Users ({users.length})
          </button>
          <button 
            className={`tab ${currentView === 'clients' ? 'active' : ''}`}
            onClick={() => setCurrentView('clients')}
          >
            Clients ({getFilteredClients().length})
          </button>
          <button 
            className={`tab ${currentView === 'pending' ? 'active' : ''}`}
            onClick={() => setCurrentView('pending')}
          >
            Pending Invitations ({pendingInvitations.length})
          </button>
        </div>

        <div className="search-controls">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : currentView === 'pending' ? (
          renderUserTable(getFilteredPendingInvitations(), true)
        ) : currentView === 'clients' ? (
          renderUserTable(getFilteredClients(), false)
        ) : (
          renderUserTable(getFilteredUsers(), false)
        )}
      </div>

      {/* Invite User Modal */}
      <Modal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          title="Invite New User"
          size="medium"
          variant="info"
          footer={
            <>
              <button
                className="btn secondary"
                onClick={() => setInviteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={handleInviteUser}
              >
                <Mail size={16} />
                Send Invitation
              </button>
            </>
          }
        >
          <div className="invite-form-new">
            <div className="form-group">
              <label htmlFor="email-new">Email Address *</label>
              <input
                id="email-new"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className={formErrors.email ? 'error' : ''}
                placeholder="user@example.com"
              />
              {formErrors.email && <span className="error-text">{formErrors.email}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName-new">First Name *</label>
                <input
                  id="firstName-new"
                  type="text"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  className={formErrors.firstName ? 'error' : ''}
                  placeholder="John"
                />
                {formErrors.firstName && <span className="error-text">{formErrors.firstName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName-new">Last Name *</label>
                <input
                  id="lastName-new"
                  type="text"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  className={formErrors.lastName ? 'error' : ''}
                  placeholder="Doe"
                />
                {formErrors.lastName && <span className="error-text">{formErrors.lastName}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone-new">Phone Number</label>
              <input
                id="phone-new"
                type="tel"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="form-group">
              <label htmlFor="role-new">Role</label>
              <select
                id="role-new"
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              >
                <option value="client">Client</option>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        </Modal>

      {/* User Details Modal */}
      {userDetailsModalOpen && selectedUser && (
        <Modal
          isOpen={userDetailsModalOpen}
          onClose={handleCloseUserDetails}
          title={isEditMode ? "Edit User" : "User Details"}
          size="large"
          variant="info"
          hasUnsavedChanges={hasUnsavedChanges}
          footer={
            isEditMode ? (
              <>
                <button
                  className="btn secondary"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={handleSaveUserChanges}
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn secondary"
                  onClick={handleCloseUserDetails}
                >
                  Close
                </button>
                <button
                  className="btn primary"
                  onClick={handleEnterEditMode}
                >
                  Edit User
                </button>
              </>
            )
          }
        >
          <div className="user-details-new">
            <div className="user-details-header">
              <div className="user-info">
                <div className="user-avatar">
                  {selectedUser.first_name && selectedUser.last_name ?
                    `${selectedUser.first_name.charAt(0)}${selectedUser.last_name.charAt(0)}` :
                    selectedUser.email.charAt(0).toUpperCase()}
                </div>
                <div className="user-meta">
                  <h3>{selectedUser.first_name && selectedUser.last_name ?
                    `${selectedUser.first_name} ${selectedUser.last_name}` :
                    selectedUser.email}</h3>
                  <div className="badges">
                    <span className={`role-badge ${getRoleBadgeClass(selectedUser.role)}`}>
                      {selectedUser.role}
                    </span>
                    <span className={`status-badge ${getStatusColor(selectedUser.status, selectedUser.tokenExpired)}`}>
                      {getStatusIcon(selectedUser.status, selectedUser.tokenExpired)}
                      {selectedUser.status === 'pending_verification' ?
                        (selectedUser.tokenExpired ? 'Expired' : 'Pending') :
                        selectedUser.status}
                    </span>
                  </div>
                </div>
              </div>
              {selectedUser.status === 'pending_verification' && (
                <div className="header-actions">
                  <button
                    className="btn secondary"
                    onClick={() => {
                      handleResendInvitation(selectedUser.id);
                      handleCloseUserDetails();
                    }}
                  >
                    <Mail size={16} />
                    Resend Email
                  </button>
                  <button
                    className="btn secondary"
                    onClick={() => {
                      handleRegenerateInvitation(selectedUser.id);
                      handleCloseUserDetails();
                    }}
                  >
                    <RefreshCw size={16} />
                    Regenerate Invitation
                  </button>
                </div>
              )}
            </div>

            <div className="details-grid-wide">
              <div className="detail-section">
                <h4>Personal Information</h4>
                {isEditMode ? (
                  <>
                    <div className="detail-item">
                      <label>First Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.first_name}
                        onChange={(e) => handleEditFormChange('first_name', e.target.value)}
                      />
                    </div>
                    <div className="detail-item">
                      <label>Last Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.last_name}
                        onChange={(e) => handleEditFormChange('last_name', e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {selectedUser.first_name && (
                      <div className="detail-item">
                        <label>First Name</label>
                        <span>{selectedUser.first_name}</span>
                      </div>
                    )}
                    {selectedUser.last_name && (
                      <div className="detail-item">
                        <label>Last Name</label>
                        <span>{selectedUser.last_name}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="detail-section">
                <h4>Contact Information</h4>
                <div className="detail-item">
                  <label>Email Address</label>
                  {isEditMode ? (
                    <input
                      type="email"
                      className="form-input"
                      value={editForm.email}
                      onChange={(e) => handleEditFormChange('email', e.target.value)}
                    />
                  ) : (
                    <span>{selectedUser.email}</span>
                  )}
                </div>
                <div className="detail-item">
                  <label>Phone Number</label>
                  {isEditMode ? (
                    <input
                      type="tel"
                      className="form-input"
                      value={editForm.phone}
                      onChange={(e) => handleEditFormChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  ) : (
                    <span>{selectedUser.phone || 'Not provided'}</span>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4>Account Information</h4>
                <div className="detail-item">
                  <label>Category</label>
                  <span className={`role-badge ${getRoleBadgeClass(selectedUser.user_type || 'client')}`}>
                    {selectedUser.user_type || 'client'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Roles</label>
                  {isEditMode ? (
                    selectedUser.user_type === 'staff' ? (
                      <div className="role-checkboxes">
                        {availableStaffRoles.map(role => (
                          <label key={role} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={editForm.roles.includes(role)}
                              onChange={() => handleToggleRole(role)}
                            />
                            <span className="checkbox-text">{role}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <span className="muted-text">Client accounts don't have assignable roles</span>
                    )
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {selectedUser.roles && selectedUser.roles.length > 0 ? (
                        selectedUser.roles.map(role => (
                          <span key={role} className={`role-badge ${getRoleBadgeClass(role)}`}>
                            {role}
                          </span>
                        ))
                      ) : (
                        <span className="muted-text">No roles assigned</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`status-badge ${getStatusColor(selectedUser.status, selectedUser.tokenExpired)}`}>
                    {getStatusIcon(selectedUser.status, selectedUser.tokenExpired)}
                    {selectedUser.status === 'pending_verification' ?
                      (selectedUser.tokenExpired ? 'Invitation Expired' : 'Pending Verification') :
                      selectedUser.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{formatDate(selectedUser.created_at)}</span>
                </div>
                <div className="detail-item">
                  <label>Last Login</label>
                  <span>{selectedUser.last_login_at ? formatDate(selectedUser.last_login_at) : 'Never'}</span>
                </div>
              </div>

              {selectedUser.status === 'pending_verification' && (
                <div className="detail-section">
                  <h4>Invitation Details</h4>
                  {selectedUser.token_expires_at && (
                    <div className="detail-item">
                      <label>Invitation Expires</label>
                      <span className={selectedUser.tokenExpired ? 'expired' : ''}>
                        {formatDate(selectedUser.token_expires_at)}
                        {selectedUser.tokenExpired && ' (Expired)'}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Invitation ID</label>
                    <span className="mono">{selectedUser.id.substring(0, 8)}...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* User Action Confirmation Modal */}
      <NotificationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        title={
          confirmationModal.type === 'deactivate' ? 'Deactivate User' : 'Activate User'
        }
        message={
          confirmationModal.user ? (
            confirmationModal.type === 'deactivate' ? 
              `Are you sure you want to deactivate ${confirmationModal.user.first_name} ${confirmationModal.user.last_name} (${confirmationModal.user.email})?\n\nThis user will no longer be able to access their account. You can reactivate them later if needed.` :
              `Are you sure you want to activate ${confirmationModal.user.first_name} ${confirmationModal.user.last_name} (${confirmationModal.user.email})?\n\nThis user will be able to access their account and use the platform.`
          ) : ''
        }
        type={
          confirmationModal.type === 'deactivate' ? 'warning' : 'success'
        }
        size="medium"
        showConfirmButton={true}
        confirmButtonText={
          confirmationModal.type === 'deactivate' ? 'Deactivate User' : 'Activate User'
        }
        cancelButtonText="Cancel"
        preventBackdropClose={true}
      />

      {/* Danger Modal for Delete Operations */}
      <DangerModal
        isOpen={dangerModal.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete User Account"
        message={
          dangerModal.user ? 
            `You are about to permanently delete ${dangerModal.user.first_name} ${dangerModal.user.last_name} (${dangerModal.user.email}).\n\nThis action cannot be undone. All user data, settings, login history, and associated records will be permanently removed from the system.` :
            ''
        }
        confirmButtonText="Delete User"
        requiresConfirmationText={true}
        confirmationText="DELETE"
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notificationModal.isOpen}
        onClose={() => setNotificationModal({ ...notificationModal, isOpen: false })}
        onConfirm={() => setNotificationModal({ ...notificationModal, isOpen: false })}
        title={notificationModal.title}
        message={notificationModal.message}
        type={notificationModal.type}
        size="medium"
      />

      <style>{`
        .user-management {
          max-width: 1400px;
          margin: 0 auto;
          padding: 24px;
        }

        /* Statistics Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: var(--border-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-medium);
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .stat-icon.active {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .stat-icon.pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .stat-icon.inactive {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .stat-number {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: var(--text-secondary);
        }

        /* Header */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .header-content h1 {
          color: var(--text-primary);
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 700;
        }

        .header-content p {
          color: var(--text-secondary);
          margin: 0;
          font-size: 16px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .invite-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: var(--accent-red);
          color: white;
          border: none;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .invite-btn:hover {
          background: var(--accent-red-dark);
          transform: translateY(-1px);
        }

        /* Controls */
        .controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .view-tabs {
          display: flex;
          gap: 4px;
        }

        .tab {
          padding: 12px 20px;
          background: none;
          border: none;
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          border-radius: var(--radius-medium);
          transition: all 0.2s ease;
        }

        .tab:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .tab.active {
          background: var(--accent-red);
          color: white;
        }

        .search-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          color: var(--text-tertiary);
        }

        .search-box input {
          padding: 10px 12px 10px 40px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          width: 300px;
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--accent-red);
        }

        /* Table */
        .table-container {
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          overflow: hidden;
        }

        .table-header {
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-secondary);
        }

        .table-row {
          display: grid;
          grid-template-columns: 1.5fr 1.5fr 1.2fr 1fr 1fr 1fr;
          gap: 16px;
          align-items: center;
          padding: 16px 20px;
        }

        .header-row {
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .data-row {
          border-bottom: 1px solid var(--border-tertiary);
          transition: background 0.2s ease;
        }

        .data-row:hover {
          background: var(--bg-secondary);
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .table-cell {
          display: flex;
          align-items: center;
        }

        /* Name and Email Cells */
        .name-cell,
        .email-cell {
          display: flex;
          align-items: center;
        }

        .user-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .user-email {
          font-size: 14px;
          color: var(--text-secondary);
          font-weight: 400;
        }

        /* Role Badges */
        .role-badge {
          padding: 4px 12px;
          border-radius: var(--radius-full);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .role-admin {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .role-staff {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .role-coach {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .role-client {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .role-default {
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        /* Status Badges */
        .status-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: var(--radius-full);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-active {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .status-pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .status-expired {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .status-inactive {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        /* Action Buttons */
        .actions-cell {
          justify-content: flex-end;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          min-width: 140px;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          position: relative;
        }

        .action-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-color: var(--border-primary);
          transform: translateY(-1px);
        }

        .action-btn:active {
          transform: translateY(0);
        }

        .action-btn.danger {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .action-btn.danger:hover {
          background: #ef4444;
          color: white;
        }

        .action-btn.success {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .action-btn.success:hover {
          background: #22c55e;
          color: white;
        }

        .action-btn.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .action-btn.warning:hover {
          background: #f59e0b;
          color: white;
        }

        .action-btn.disabled {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          cursor: not-allowed;
        }

        .action-btn.disabled:hover {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          transform: none;
        }

        /* Loading and Empty States */
        .loading {
          text-align: center;
          padding: 64px;
          color: var(--text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: 64px 32px;
          color: var(--text-secondary);
        }

        .empty-state svg {
          margin-bottom: 16px;
          color: var(--text-tertiary);
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          color: var(--text-primary);
          font-size: 20px;
        }

        .empty-state p {
          margin: 0;
          max-width: 400px;
          margin: 0 auto;
          line-height: 1.5;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 0 0 1px var(--border-primary);
        }

        .modal.wide {
          max-width: 800px;
          max-height: 85vh;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          color: var(--text-primary);
          font-weight: 500;
          font-size: 14px;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--accent-red);
        }

        .form-group input.error {
          border-color: #ef4444;
        }

        .error-text {
          display: block;
          color: #ef4444;
          font-size: 12px;
          margin-top: 4px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* New standardized modal form wrapper - matches CardDetailsModal pattern */
        .invite-form-new,
        .user-details-new {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .invite-form-new .form-group,
        .user-details-new .form-group {
          margin-bottom: 20px;
        }


        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn.primary {
          background: var(--accent-red);
          color: white;
        }

        .btn.primary:hover {
          background: var(--accent-red-dark);
        }

        .btn.secondary {
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .btn.secondary:hover {
          background: var(--border-secondary);
          color: var(--text-primary);
        }

        .btn.danger {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .btn.danger:hover {
          background: #ef4444;
          color: white;
        }

        /* User Details Modal Styles */
        .user-details-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 20px;
          border-bottom: 1px solid var(--border-secondary);
          margin-bottom: 24px;
        }

        .user-details-header .user-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--accent-red);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .user-meta h3 {
          margin: 0 0 8px 0;
          color: var(--text-primary);
          font-size: 20px;
          font-weight: 600;
        }

        .badges {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .details-grid {
          display: grid;
          gap: 24px;
        }

        .details-grid-wide {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .detail-section {
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
          padding: 20px;
        }

        .detail-section h4 {
          margin: 0 0 16px 0;
          color: var(--text-primary);
          font-size: 16px;
          font-weight: 600;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-tertiary);
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .detail-item label {
          color: var(--text-secondary);
          font-weight: 500;
          font-size: 14px;
        }

        .detail-item span {
          color: var(--text-primary);
          font-weight: 500;
        }

        .detail-item span.expired {
          color: #ef4444;
        }

        .detail-item .form-input {
          flex: 1;
          max-width: 400px;
          padding: 8px 12px;
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .detail-item .form-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .role-checkboxes {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 6px 12px;
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          transition: all 0.2s ease;
        }

        .checkbox-label:hover {
          background: var(--border-primary);
          border-color: var(--accent-primary);
        }

        .checkbox-label input[type="checkbox"] {
          cursor: pointer;
          width: 16px;
          height: 16px;
          accent-color: var(--accent-primary);
        }

        .checkbox-text {
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .modal-actions-inline {
          display: flex;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--border-secondary);
        }

        .modal-actions-inline .btn {
          flex: 1;
        }

        .mono {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
          background: var(--bg-tertiary);
          padding: 2px 6px;
          border-radius: 4px;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .table-row {
            grid-template-columns: 1.5fr 1.5fr 1.2fr 1fr 1fr;
          }

          .table-row .table-cell:nth-child(5) {
            display: none;
          }
        }

        @media (max-width: 900px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .table-row {
            grid-template-columns: 1.5fr 1.5fr 1.2fr 1fr;
          }

          .table-row .table-cell:nth-child(4) {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .user-management {
            padding: 16px;
          }
          
          .header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .controls {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .search-box input {
            width: 100%;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .table-row {
            grid-template-columns: 1fr 1.2fr 1fr;
            gap: 8px;
            padding: 12px 16px;
          }

          .table-row .table-cell:nth-child(2) {
            display: none;
          }

          .table-row .table-cell:nth-child(3) {
            display: none;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }

      `}</style>
    </div>
  );
};
